import { Button, Link } from '@chakra-ui/react';
import { isToday } from 'date-fns';
import mixpanel from 'mixpanel-browser';

import { useContactSalesMutation, useGetLoggedInUser } from '@revelio/auth';
import { CTAInteractionTrackingEvents, type PageTitles } from '@revelio/core';

import { useToast } from '../../shared/hooks/useToast';
import {
  toastContactSalesAlreadyNotified,
  toastContactSalesRequestError,
  toastContactSalesRequestSuccess,
} from './contact-sales-toast';
import { useFreeTrialAlertModalState } from './useFreeTrialAlertModalState';

export interface ContactSalesButtonProps {
  buttonProps?: React.ComponentProps<typeof Button>;
  page?: PageTitles;
  onClick?: () => void;
  isLink?: boolean;
}

export function ContactSalesButton({
  buttonProps,
  page,
  onClick,
  isLink = false,
}: ContactSalesButtonProps) {
  const toast = useToast();
  const { loggedInUser } = useGetLoggedInUser();
  const { email } = loggedInUser;
  const { days } = useFreeTrialAlertModalState();

  const mixpanelContactSales = () => {
    mixpanel.track(CTAInteractionTrackingEvents.CONTACT_SALES_CLICK, {
      days_left: days,
      page,
    });
  };

  const { mutate: contactSales } = useContactSalesMutation();

  const handleContactSales = () => {
    mixpanelContactSales();
    const runOnClick = () => {
      if (typeof onClick === 'function') {
        onClick();
      }
    };
    const contactedSales = localStorage.getItem('contacted-sales') || '';
    if (!contactedSales || !isToday(new Date(contactedSales))) {
      contactSales(
        { email, trialUpgrade: 'expiration' },
        {
          onSuccess: () => {
            localStorage.setItem('contacted-sales', new Date().toDateString());
            toastContactSalesRequestSuccess(toast);
          },
          onError: () => {
            toastContactSalesRequestError(toast);
          },
          onSettled: runOnClick,
        }
      );
    } else {
      toastContactSalesAlreadyNotified(toast);
      runOnClick();
    }
  };

  return isLink ? (
    <Link
      variant="tooltip"
      onClick={handleContactSales}
      data-testid="contact-sales-link"
    >
      Contact Sales
    </Link>
  ) : (
    <Button
      colorScheme="green"
      onClick={handleContactSales}
      {...buttonProps}
      data-testid="contact-sales-button"
    >
      Contact Sales
    </Button>
  );
}

export default ContactSalesButton;
